import React, { useState } from 'react';
import {
  PlayCircle,
  Database,
  Settings,
  ChevronDown,
  ChevronUp,
  Mail,
  Cloud,
  Zap,
  CheckCircle,
  FileText,
  ExternalLink,
} from 'lucide-react';
import { useTheme } from '../../../components/common/Theme/ThemeContext';
import DetailedNodeLogModal from './DetailedNodeLogModal';

export interface TimelineEntry {
  id: string;
  name: string;
  type: 'start' | 'load' | 'agent' | 'condition' | 'branch' | 'api' | 'email' | 'end';
  status: 'Success' | 'Failed' | 'Running';
  time: string;
  input: string;
  output: string;
  nested?: TimelineEntry[];
  /** For category-detail API (from timeline row). */
  categoryType?: string | null;
  categoryId?: string | null;
}

const iconMap = {
  start: PlayCircle,
  load: Database,
  agent: Settings,
  condition: FileText,
  branch: Zap,
  api: Cloud,
  email: Mail,
  end: CheckCircle,
};

export interface WorkflowTimelineProps {
  entries: TimelineEntry[];
  /** When set with runId, "View full" loads category-detail from the run-log service. */
  workflowId?: string;
  runId?: string;
}

function normalizedCategoryTypeKey(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase().replace(/-/g, '_');
}

function isHtmlViewerTimelineEntry(entry: TimelineEntry): boolean {
  return normalizedCategoryTypeKey(entry.name) === 'html_viewer';
}

function buildWorkflowRunHtmlViewerUrl(workflowId: string, runId: string, nodeId: string): string {
  const w = encodeURIComponent(workflowId.trim());
  const r = encodeURIComponent(runId.trim());
  const n = encodeURIComponent(nodeId.trim());
  return `${window.location.origin}/workflow-run-html/${w}/${r}/${n}`;
}

const WorkflowTimeline: React.FC<WorkflowTimelineProps> = ({ entries, workflowId, runId }) => {
  
  const { theme } = useTheme();
  const isLight = theme === 'white';

  const statusText = {
    Success: isLight ? 'text-emerald-700' : 'text-emerald-500',
    Failed: isLight ? 'text-rose-600' : 'text-rose-400',
    Running: isLight ? 'text-amber-700' : 'text-amber-400',
  } as const;

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggleExpand = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const [selectedNodeLog, setSelectedNodeLog] = useState<TimelineEntry | null>(null);
  const [showFullLog, setShowFullLog] = useState(false);

  const rail = isLight ? 'border-slate-300' : 'border-slate-600/80';
  const dotWrap = isLight
    ? 'border-slate-200 bg-white shadow-sm'
    : 'border-slate-700 bg-slate-900 shadow-sm';
  const title = isLight ? 'text-slate-900' : 'text-slate-50';
  const meta = isLight ? 'text-slate-500' : 'text-slate-500';
  const card = isLight
    ? 'border-slate-200 bg-slate-50'
    : 'border-slate-700/80 bg-slate-950/40';
  const label = isLight ? 'text-slate-500' : 'text-slate-500';
  const mono = isLight ? 'text-slate-700' : 'text-slate-300';
  const divider = isLight ? 'border-slate-200' : 'border-slate-800';
  const nestedBtn = isLight
    ? 'text-slate-600 hover:text-slate-900'
    : 'text-slate-500 hover:text-slate-200';
  const viewFull = isLight
    ? 'border-slate-300 bg-white text-slate-800 hover:border-blue-500 hover:bg-slate-50'
    : 'border-slate-600 bg-slate-800/80 text-slate-100 hover:border-blue-500/40 hover:bg-slate-700';
  const viewInBrowser = isLight
    ? 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900'
    : 'border-slate-600/80 bg-slate-900/60 text-slate-300 hover:border-slate-500 hover:bg-slate-800 hover:text-slate-100';
  const unavailable = isLight ? 'text-slate-400' : 'text-slate-600';

  return (
    <div className="max-h-[65vh] space-y-4 overflow-y-auto px-3 py-3 sm:px-4">
      {entries.map((entry) => {
        const Icon = iconMap[entry.type] || FileText;
        const showHtmlViewerLink =
          isHtmlViewerTimelineEntry(entry) &&
          workflowId?.trim() &&
          runId?.trim() &&
          entry.id?.trim();
        console.log('showHtmlViewerLink', entry);
        return (
          <div key={entry.id} className={`relative flex items-start gap-3 border-l-2 pl-6 ${rail}`}>
            <span className={`absolute left-0 top-2 -ml-[9px] rounded-full border p-1 ${dotWrap}`}>
              <Icon className={`h-4 w-4 ${statusText[entry.status]}`} />
            </span>
            <div className="w-full min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                  <div className={`min-w-0 text-sm font-semibold leading-snug ${title}`}>{entry.name}</div>
                  {showHtmlViewerLink ? (
                    <button
                      type="button"
                      onClick={() => {
                        const w = workflowId?.trim();
                        const r = runId?.trim();
                        const n = entry.id?.trim();
                        if (!w || !r || !n) return;
                        const url = buildWorkflowRunHtmlViewerUrl(w, r, n);
                        window.open(url, '_blank', 'noopener,noreferrer');
                      }}
                      className={`inline-flex shrink-0 items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-semibold transition ${viewInBrowser}`}
                      title="Open HTML viewer for this run in a new tab"
                    >
                      <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
                      View in browser
                    </button>
                  ) : null}
                </div>
                <div className={`shrink-0 text-xs font-semibold ${statusText[entry.status]}`}>{entry.status}</div>
              </div>
              <div className={`mt-0.5 text-[11px] ${meta}`}>{entry.time}</div>

              <div className={`mt-2 rounded-lg border p-3 text-xs ${card}`}>
                <div className={`text-[10px] font-semibold uppercase tracking-wide ${label}`}>Input</div>
                <div className={`mt-1 truncate font-mono ${mono}`} title={entry.input}>
                  {entry.input}
                </div>
                <div className={`mt-2 text-[10px] font-semibold uppercase tracking-wide ${label}`}>Output</div>
                <div className={`mt-1 truncate font-mono ${mono}`} title={entry.output}>
                  {entry.output}
                </div>

                <div className={`mt-3 flex justify-end border-t pt-2 ${divider}`}>
                  {workflowId && runId ? (
                    <button
                      type="button"
                      className={`rounded-md border px-2.5 py-1.5 text-[11px] font-semibold transition ${viewFull}`}
                      onClick={() => {
                        setSelectedNodeLog(entry);
                        setShowFullLog(true);
                      }}
                    >
                      View full
                    </button>
                  ) : (
                    <span className={`text-[11px] ${unavailable}`}>View full unavailable</span>
                  )}
                </div>
              </div>

              {entry.nested && entry.nested.length > 0 && (
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => toggleExpand(entry.id)}
                    className={`flex items-center gap-1 text-xs font-medium transition ${nestedBtn}`}
                  >
                    {expanded[entry.id] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    Nested logs
                  </button>
                  {expanded[entry.id] && (
                    <div className="ml-4 mt-2">
                      <WorkflowTimeline entries={entry.nested} workflowId={workflowId} runId={runId} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {showFullLog && selectedNodeLog && workflowId && runId && (
        <DetailedNodeLogModal
          nodeName={selectedNodeLog.name}
          nodeTime={selectedNodeLog.time}
          status={selectedNodeLog.status}
          onClose={() => setShowFullLog(false)}
          categoryDetail={{
            workflowId,
            runId,
            nodeId: selectedNodeLog.id,
            categoryType: selectedNodeLog.categoryType,
            categoryId: selectedNodeLog.categoryId,
          }}
        />
      )}
    </div>
  );
};

export default WorkflowTimeline;
